import React from "react";
import IContact from "../../models/IContact";
import { TableRow, TableCell, Button } from "@material-ui/core";
import styled from "styled-components";
import Services from "../../services/Services";
import { IApplicationState } from "../../store";
import { connect } from "react-redux";
import uuid from "uuid/v4";
import { selectAuth } from "../../store/auth/selectors";
import { selectStatusDispatch } from "../../store/status/selectors";
import { IPendingItem } from "../../store/status/types";
import snackbarService from "../../services/ui-messaging/SnackbarService";
import { selectCompany } from "../../store/context/selectors";
import LinkButton from "../LinkButton";

const createItem = (isAdd: boolean, fullName: string): IPendingItem => ({
  id: uuid(),
  description: `${isAdd ? "Activating" : "Inactivating"} contact ${fullName}`,
});

const createResendItem = (fullName: string): IPendingItem => ({
  id: uuid(),
  description: `Resending signup email for '${fullName}'`,
});

const createGrantAccessItem = (fullName: string): IPendingItem => ({
  id: uuid(),
  description: `Granting Portal Access to ${fullName}`,
});

const Row = styled(TableRow)<{ active: boolean }>`
  ${props =>
    !props.active &&
    `
    opacity: 0.5;
  `}
`;

const CellButton = styled(Button)`
  margin-left: 1em;
  min-width: 70px;
`;

const AccessButton = styled(Button)`
  margin-left: 1em;
  min-width: 200px;
`;

const ActivationButton = styled(Button)`
  margin-left: 1em;
  min-width: 120px;
`;

const ActionCell = styled(TableCell)`
  display: flex;
  justify-content: flex-end;
`;

interface IContactRowProps extends IContact {
  token: string;
  addPending: Function;
  completePending: Function;
  onRequestRefresh: Function;
  type: string;
}

const ContactRow = (props: IContactRowProps) => {
  const {
    ID,
    CompanyID,
    Active,
    LastName,
    FirstName,
    EmailAddress,
    FullName,
    PartnerPortalUser,
    type,
    addPending,
    completePending,
    onRequestRefresh,
    token,
  } = props;

  const handleInactivate = async () => {
    const item = createItem(false, FullName);
    try {
      if (!ID) {
        throw new Error("Unable to dectivate a user without an ID");
      }

      addPending(item);
      const contactRepo = await new Services().ContactRepository();
      await contactRepo.inactivate(token, CompanyID, ID);
      completePending(item);
      snackbarService.success(`User '${FullName}' Deactivated`);
      onRequestRefresh();
    } catch (err) {
      completePending(item);
      snackbarService.error(err);
    }
  };

  const handleActivate = async () => {
    const item = createItem(true, FullName);
    try {
      if (!ID) {
        throw new Error("Unable to activate a user without an ID");
      }

      addPending(item);
      const contactRepo = await new Services().ContactRepository();
      await contactRepo.activate(token, CompanyID, ID);
      completePending(item);
      snackbarService.success(`User '${FullName}' Activated`);
      onRequestRefresh();
    } catch (err) {
      completePending(item);
      snackbarService.error(err);
    }
  };

  const handleResend = async () => {
    const item = createResendItem(FullName);
    try {
      if (!ID) {
        throw new Error("Unable to activate a user without an ID");
      }

      addPending(item);
      const signupRepo = await new Services().SignUpRepository();
      await signupRepo.CompleteContactSignUp(token, props);
      completePending(item);
      snackbarService.success(`Email resent to '${FullName}'`);
      onRequestRefresh();
    } catch (err) {
      completePending(item);
      snackbarService.error(err);
    }
  };

  const handleGrantAccess = async () => {
    const item = createGrantAccessItem(FullName);
    try {
      if (!ID) {
        throw new Error("Unable to grant access to a user without an ID");
      }

      addPending(item);
      const contactRepo = await new Services().ContactRepository();
      await contactRepo.grantAccess(token, CompanyID, ID);
      completePending(item);
      snackbarService.success(`User '${FullName}' Granted Portal Access`);
      onRequestRefresh();
    } catch (err) {
      completePending(item);
      snackbarService.error(err);
    }
  };

  return (
    <Row key={ID} active={Active} hover>
      <TableCell>{type == "Vendor" ? <LinkButton to={`/users/details/${ID}`}>{ID}</LinkButton> : ID}</TableCell>
      <TableCell>{LastName}</TableCell>
      <TableCell>{FirstName}</TableCell>
      <TableCell>{EmailAddress}</TableCell>
      <ActionCell>
        {Active ? (
          <>
            <CellButton variant="outlined" color="secondary" onClick={handleInactivate}>
              Deactivate
            </CellButton>

            {PartnerPortalUser ? (
              <AccessButton variant="outlined" onClick={handleResend}>
                Resend Signup Email
              </AccessButton>
            ) : (
              <AccessButton variant="outlined" onClick={handleGrantAccess}>
                Grant Portal Access
              </AccessButton>
            )}
          </>
        ) : (
          <ActivationButton variant="outlined" color="primary" onClick={handleActivate}>
            Activate
          </ActivationButton>
        )}
      </ActionCell>
    </Row>
  );
};

export default connect(
  (state: IApplicationState) => ({
    ...selectAuth(state),
    company: selectCompany(state),
  }),
  dispatch => ({
    ...selectStatusDispatch(dispatch),
  }),
)(ContactRow);
